import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/apiConfig';
import { FaToggleOn, FaToggleOff, FaSave, FaSpinner, FaHistory } from 'react-icons/fa';

const MaintenanceManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({
    isEnabled: false,
    message: '',
    updatedAt: null
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/maintenance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStatus({
           ...response.data.data,
           isEnabled: response.data.data.isEnabled || false
        });
      }
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
      toast.error('Failed to load maintenance settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    setStatus(prev => ({ ...prev, isEnabled: !prev.isEnabled }));
  };

  const handleMessageChange = (e) => {
    setStatus(prev => ({ ...prev, message: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/maintenance/toggle`, {
        isEnabled: status.isEnabled,
        message: status.message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`Maintenance mode turned ${status.isEnabled ? 'ON' : 'OFF'}`);
        setStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      toast.error(error.response?.data?.message || 'Failed to update maintenance settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-blue-600 text-3xl" />
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className={`p-6 sm:p-10 ${status.isEnabled ? 'bg-red-50' : 'bg-green-50'} transition-colors duration-500`}>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 mb-2">System Maintenance</h1>
              <p className="text-gray-600">Control global access to the application for updates or fixes.</p>
            </div>
            <div className="flex items-center gap-4">
               <span className={`text-sm font-bold uppercase tracking-widest ${status.isEnabled ? 'text-red-600' : 'text-green-600'}`}>
                 {status.isEnabled ? 'Active' : 'Inactive'}
               </span>
               <button 
                 onClick={handleToggle}
                 className={`text-5xl transition-all duration-300 transform hover:scale-110 ${status.isEnabled ? 'text-red-500' : 'text-gray-300'}`}
               >
                 {status.isEnabled ? <FaToggleOn /> : <FaToggleOff />}
               </button>
            </div>
          </div>
        </div>

        <div className="p-6 sm:p-10">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-4">
              <label className="block text-lg font-bold text-gray-800">
                Maintenance Message
              </label>
              <p className="text-sm text-gray-500 mb-4">
                This message will be displayed to all users while the system is in maintenance mode.
              </p>
              <textarea
                value={status.message}
                onChange={handleMessageChange}
                className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all outline-none text-gray-700 min-h-[150px]"
                placeholder="We are currently upgrading our systems. Please check back in an hour..."
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center bg-gray-50 p-6 rounded-xl border border-gray-100">
              <div className="flex items-center gap-3 text-gray-500 mb-4 sm:mb-0">
                <FaHistory />
                <span className="text-sm font-medium">
                  Last Updated: {status.updatedAt ? new Date(status.updatedAt).toLocaleString() : 'Never'}
                </span>
              </div>
              <button
                type="submit"
                disabled={saving}
                className={`flex items-center gap-3 px-10 py-4 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-95 disabled:opacity-50 ${status.isEnabled ? 'bg-red-600 hover:bg-red-700 shadow-red-200' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'}`}
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {saving ? 'Updating...' : 'Save Configuration'}
              </button>
            </div>
          </form>

          {status.isEnabled && (
             <div className="mt-10 p-6 bg-orange-50 rounded-xl border-l-8 border-orange-500 animate-pulse">
               <h3 className="text-orange-800 font-bold mb-2 uppercase tracking-wide">⚠️ Operational Warning</h3>
               <p className="text-orange-700 text-sm">
                 The application is currently blocked for all users. Only the Admin Panel remains accessible. Remember to turn this off once maintenance is complete.
               </p>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceManagement;
